import { Link } from 'react-router-dom';
import Navbar2 from '@/component/navbar2';
import BrandFooter from '@/components/BrandFooter';
import Stars from '@/components/Stars';
import TeamAvatar from '@/components/TeamAvatar';
import { L7_MEDIA } from '@/data/landingMedia';
import {
  HTReveal, SHead, AudienceGrid, StepList, AppFrame, HumanBand, Stats, ClientStory, Faq, Ck, CtaBand,
  ONLINE_AUDIENCE,
} from '@/components/landing/HT';
import { BadgeCheck, Building2, CalendarDays, CheckCircle2, FileText, Gem } from 'lucide-react';
import { nextVatWindow } from '@/utils/filingDates';

/* ============================================================================
   Conncct Run & Comply — connectcorporate.com home page.

   Build of the combined reference landing (setup + accounting + tax + corporate
   + legal, "human touch" design). Shared chrome (Navbar2 / BrandFooter) replaces
   the prototype's own header/footer and therefore sits OUTSIDE the `.ht` wrapper,
   whose scoped reset would otherwise clobber it.

   Two things are kept from the page this replaced, because the reference deck
   simply doesn't cover them and deleting them would lose real content:
     · the FAQ (re-dressed in the reference accordion), and
     · a "start where you need us" row, which carries the routing the old pricing
       preview used to do (/corporate, /accounting, /private).

   Section CSS + shared blocks: styles/landingHT.css, components/landing/HT.
   App.tsx renders this at "/" when BRAND === 'corporate'.
   ========================================================================== */

const PILLS = [
  { label: 'Creators', dot: '#F0776B' },
  { label: 'Developers', dot: '#5B8DEF' },
  { label: 'Designers', dot: '#E85C9A' },
  { label: 'Online educators', dot: '#F0A640' },
  { label: 'Consultants', dot: '#7A4FC0' },
  { label: 'E-commerce', dot: '#15824E' },
  { label: 'SaaS & agencies', dot: '#2E6BD6' },
];

type Pillar = { cls: 'corp' | 'acc' | 'legal'; kicker: string; h: string; d: string; items: [string, string][] };

const PILLARS: Pillar[] = [
  {
    cls: 'corp', kicker: 'Part 1 · Corporate services', h: 'Set up & run your company',
    d: 'Mainland & free zone formation and everything that keeps it running.',
    items: [
      ['Company formation', 'Mainland (DED) & free zone — from AED 17,850'],
      ['Visas & residence', 'Founder, employee, family & Golden Visa'],
      ['PRO, renewals & WPS payroll', 'Nothing lapses — a managed calendar'],
    ],
  },
  {
    cls: 'acc', kicker: 'Part 2 · Accounting & tax', h: 'Books, VAT & Corporate Tax',
    d: 'Registered, filed and kept FTA-compliant — never a missed deadline.',
    items: [
      ['Bookkeeping', 'Monthly, priced by volume — from AED 1,500/mo'],
      ['VAT & Corporate Tax', '5% VAT · 9% CT — registration & filing to the FTA'],
      ['Statements & audit', 'CFO-ready, investor-grade reporting'],
    ],
  },
  {
    cls: 'legal', kicker: 'Part 3 · Legal', h: 'Protected as you grow',
    d: 'The legal back office founders forget — until they need it.',
    items: [
      ['Contract drafting & review', 'Employment, vendor & partnership agreements'],
      ['Regulatory compliance advisory', 'UAE corporate law, data protection, trade rules'],
      ['Disputes & case strategy', 'Cross-border litigation & risk management'],
    ],
  },
];

const STEPS = [
  { h: 'You invoice & upload expenses', p: 'From your phone or laptop, in seconds — with tax settings pre-configured for international platforms (Stripe, YouTube, Amazon, App Store).' },
  { h: 'Your accountant reviews & guides you', p: 'We answer by chat, check your invoices and make sure you never miss a deduction — a real person, not a ticket queue.' },
  { h: 'You confirm, we file to the FTA', p: 'See your VAT and Corporate Tax calculated, confirm in one click, and we submit them to the FTA on time.' },
];

const FAQS = [
  { q: 'Mainland or free zone — which do I need?', a: 'It depends on who your customers are. If you want to invoice UAE companies and government freely, mainland usually wins. If you’re serving clients abroad and want lower cost and full ownership, a free zone is often better. Tell us your business model and we’ll tell you which one — before you pay anything.' },
  { q: 'How long does setup take?', a: 'A free zone licence is typically a few working days once documents are in. Mainland takes a little longer because of approvals. Visas run in parallel and add roughly one to three weeks depending on medical and Emirates ID slots.' },
  { q: 'Do I really need to register for corporate tax?', a: 'Almost certainly yes. UAE corporate tax registration applies to virtually every licensed entity — including free zone companies and even businesses making no profit. The relief and the 0% bands are claimed through the return, not by skipping registration. Missing it carries penalties.' },
  { q: 'Can you take over books that are already a mess?', a: 'Yes — that’s a large part of what we do. We’ll do a free check of where you stand, tell you honestly what’s missing, and quote to bring you current before we start the monthly work.' },
  { q: 'Are your fees really fixed?', a: 'Yes. Your price is built from your actual profile — transactions, VAT status, tax status, jurisdiction — and shown to you upfront. If your business genuinely changes scale we’ll tell you and re-agree it with you. It does not appear on an invoice as a surprise.' },
  { q: 'What is Conncct Private?', a: 'It’s for clients whose affairs don’t fit a pricing page: several entities, a family office, a group structure, or simply a preference for one senior person who knows everything and says little. It’s by invitation and enquiry rather than sign-up.' },
  { q: 'Do you do consulting or fundraising too?', a: 'Conncct Grow & Raise covers advisory and capital readiness. It carries the same brand but is a separate legal entity from the accounting and corporate services business — different company, same standards.' },
];

export default function RunComply() {
  const vat = nextVatWindow();

  return (
    <>
      <Navbar2 logoTo="/" signupTo="/signup" />

      <div className="ht">
        <HTReveal />

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <section className="hero">
          <div className="wrap hgrid">
            <div className="rv">
              <span className="eyebrow">Setup · Accounting · Tax · Corporate · Legal — UAE</span>
              <h1>The business side of your startup — <span className="g">handled.</span></h1>
              <p className="sub">
                Company setup, accounting, VAT, Corporate Tax, visas and legal — one team, one platform,
                one published price. <b>You build the business. We handle the paperwork and the FTA.</b>
              </p>
              <div className="pills">
                {PILLS.map(p => <span className="pill" key={p.label}><span className="d" style={{ background: p.dot }} />{p.label}</span>)}
              </div>
              <div className="hero-cta">
                <Link to="/book-consultation" className="btn btn-y">Talk to Conncct <span className="arr">→</span></Link>
                <Link to="/accounting/pricing" className="btn btn-o">See pricing</Link>
              </div>
              <div className="trust">
                <span className="rate"><Stars /> <span><b style={{ color: 'var(--ink)' }}>Built by the team behind Wealthface</b> · 300+ businesses supported</span></span>
                <span className="badge"><BadgeCheck size={15} color="#15824E" /> Filed by registered FTA tax agents</span>
              </div>
            </div>

            <div className="visual rv d1">
              <div className="photo"><img src={L7_MEDIA.accountingClient} alt="" loading="lazy" /></div>

              <div className="fcard c1">
                <div className="t"><CheckCircle2 size={14} color="#15824E" /> Taxes up to date</div>
                <div className="s">VAT · last quarter</div>
                <span className="tagpill g">Filed to FTA</span>
              </div>
              <div className="fcard c2">
                <div className="t"><CalendarDays size={14} color="var(--amber)" /> Next filing</div>
                <div className="s">{vat.dueLabel}</div>
                <span className="tagpill a">{vat.daysLeft} days left</span>
              </div>
              <div className="fcard c3">
                <div className="t">Summary · last 12 months</div>
                <div className="mini">
                  <div><b style={{ color: '#2E6BD6' }}>AED 292K</b><span>Income</span></div>
                  <div><b style={{ color: '#7A4FC0' }}>AED 61K</b><span>Expenses</span></div>
                  <div><b style={{ color: '#15824E' }}>AED 231K</b><span>Profit</span></div>
                </div>
              </div>
              <div className="fcard c4">
                <TeamAvatar name="Malak" initials="M" className="av" />
                <div><div className="t">Your accountant, Malak</div><span className="dotline">Online — here to help</span></div>
              </div>
              <div className="fcard c5">
                <span className="ic"><CheckCircle2 size={16} /></span>
                <div><div className="t">All under control</div><div className="s">We handle it, you run your business.</div></div>
              </div>
            </div>
          </div>
        </section>

        {/* ── AUDIENCE ─────────────────────────────────────────────────── */}
        <section className="sec alt">
          <div className="wrap">
            <SHead
              k="For digital professionals"
              h2="If your business is online, we know how it’s taxed."
              lead="Digital income has its own rules — foreign platforms, cross-border clients, marketplaces. We’re set up for exactly that."
            />
            <AudienceGrid items={ONLINE_AUDIENCE} />
          </div>
        </section>

        {/* ── THREE PILLARS ────────────────────────────────────────────── */}
        <section className="sec" id="services">
          <div className="wrap">
            <SHead
              k="Everything, one team"
              h2={<>Set up, stay compliant, and stay protected — <span style={{ color: 'var(--amber)' }}>under one roof</span>.</>}
              lead="Three parts of your back office, run by the same team on the same platform. No juggling a formation agent, a bookkeeper, a tax agent and a lawyer."
            />
            <div className="pillars">
              {PILLARS.map((p, i) => (
                <div className={`pcol ${p.cls} rv`} style={{ transitionDelay: `${i * 80}ms` }} key={p.kicker}>
                  <div className="pk">{p.kicker}</div>
                  <h3>{p.h}</h3>
                  <div className="pd">{p.d}</div>
                  <ul className="plist">
                    {p.items.map(([b, s]) => (
                      <li key={b}><span className="ck"><Ck size={12} /></span><div>{b}<span className="sub">{s}</span></div></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="oneroof rv">
              One login. One team. One published price — <b>setup → compliance → legal</b>, handled.
            </div>
          </div>
        </section>

        {/* ── HOW WE WORK ──────────────────────────────────────────────── */}
        <section className="sec alt" id="how">
          <div className="wrap">
            <SHead
              k="How we work"
              h2="A real online back office. No emails, no paperwork, no waiting."
              lead="Everything connected on one platform — your invoices, expenses, taxes and your dedicated accountant, in the same place."
            />
            <div className="howwrap">
              <AppFrame nav={['Home', 'Income', 'Expenses', 'Taxes', 'Chat support', 'Deductions']}>
                <div className="drow">
                  <div className="l"><b>Next tax filing</b><span>{vat.windowLabel} · {vat.daysLeft} days left</span></div>
                  <Link to="/book-consultation" className="btn btn-o" style={{ padding: '8px 14px', fontSize: 12 }}>Add to calendar</Link>
                </div>
                <div className="drow">
                  <div className="l"><b>Next taxes forecast</b><span>{vat.quarterLabel}</span></div>
                  <div className="amt a">AED 13,391</div>
                </div>
                <div className="drow">
                  <div className="l"><b>Saved via deductions</b><span>{vat.year} to date</span></div>
                  <div className="amt p">AED 3,320</div>
                </div>
                <div className="dkpi">
                  <div style={{ background: '#E7EEFB' }}><b style={{ color: '#2E6BD6' }}>282K</b><span style={{ color: '#2E6BD6' }}>Income</span></div>
                  <div style={{ background: '#F1E9FB' }}><b style={{ color: '#7A4FC0' }}>119K</b><span style={{ color: '#7A4FC0' }}>Expenses</span></div>
                  <div style={{ background: '#E4F3EA' }}><b style={{ color: '#15824E' }}>163K</b><span style={{ color: '#15824E' }}>Profit</span></div>
                </div>
              </AppFrame>
              <StepList steps={STEPS} />
            </div>
          </div>
        </section>

        {/* ── HUMAN TOUCH ──────────────────────────────────────────────── */}
        <section className="sec">
          <div className="wrap">
            <HumanBand
              title="A real accountant on your side — not a ticket number."
              lead="Every founder gets a named, dedicated accountant who knows your business and answers in minutes. Software does the busywork; a human makes the calls that matter."
              feats={['Dedicated accountant', 'Fast chat replies', 'Proactive deadline alerts', 'Human review on every filing']}
              adviser="Malak"
              role="your accountant"
              avatar={<TeamAvatar name="Malak" initials="M" style={{ width: '100%', height: '100%' }} />}
              chat={[
                { from: 'them', text: 'Hi! I noticed your Stripe payouts jumped this quarter — want me to review your VAT position before the deadline?' },
                { from: 'you', text: 'Yes please' },
                { from: 'them', text: 'Done. You’re covered, and I found AED 1,200 in deductions you’d missed. All set for the filing window.' },
              ]}
            />
          </div>
        </section>

        {/* ── PROOF ────────────────────────────────────────────────────── */}
        <section className="sec alt">
          <div className="wrap">
            <SHead k="Real results" h2="Founders across the UAE already trust their back office to Conncct." />
            <Stats items={[
              { b: '300+', s: 'Businesses supported by our team' },
              { b: '40+', s: 'Free zones & mainland covered' },
              { b: '3', s: 'Service lines under one roof' },
              { b: '0', s: 'Missed FTA deadlines on managed clients' },
            ]} />
            <ClientStory
              quote="Honestly a lifesaver. I had no idea where to start with tax and was completely lost — until I found Conncct."
              name="Lara B."
              note="Content creator · Dubai"
              initial="L"
              photo={L7_MEDIA.runComplyHuman}
              caption="Setup, books and filings — run by one team, on one file."
            />
          </div>
        </section>

        {/* ── START WHERE YOU NEED US ──────────────────────────────────── */}
        <section className="sec">
          <div className="wrap">
            <SHead k="Start where you need us" h2="Pick the part you need first. Add the rest whenever." />
            <div className="aud" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))' }}>
              {[
                { to: '/corporate', icon: <Building2 size={21} />, bg: '#FCE4D6', fg: '#D9662E', t: 'Corporate services', s: 'Company setup · visas · PRO' },
                { to: '/accounting', icon: <FileText size={21} />, bg: '#FFF3CE', fg: '#E6B400', t: 'Accounting & tax', s: 'Bookkeeping · VAT · Corporate Tax' },
                { to: '/private', icon: <Gem size={21} />, bg: '#E7EEFB', fg: '#2E6BD6', t: 'Conncct Private', s: 'Multi-entity & family office' },
              ].map((c, i) => (
                <Link className="acard rv" style={{ transitionDelay: `${i * 60}ms` }} to={c.to} key={c.to}>
                  <span className="ic" style={{ background: c.bg, color: c.fg }}>{c.icon}</span>
                  <h3>{c.t}</h3>
                  <p>{c.s}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className="sec alt" id="faq">
          <div className="wrap">
            <SHead k="Questions, answered" h2="The questions everyone asks first." />
            <Faq items={FAQS} />
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────────── */}
        <CtaBand
          title="Your taxes, sorted. Your business, under control."
          body="Book a free call and we’ll map exactly what your business needs to register, file and stay FTA-compliant — with a real accountant from day one."
          action={<Link to="/book-consultation" className="btn btn-y">Book a free call <span className="arr">→</span></Link>}
        />
      </div>

      <BrandFooter />
    </>
  );
}
